import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { Mic } from 'lucide-react';
import api from '../../utils/authUtils';
import userManagementImage from '../../assets/images/user_management.webp';
import contentManagementImage from '../../assets/images/content_management.webp';

const PageContainer = styled.div`
  min-height: 80vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: transparent;
`;

const MainContent = styled.main`
  width: 100%;
  max-width: 800px;
  padding: 2rem;
`;

const DashboardTitle = styled.h1`
  font-size: 2.25rem;
  font-weight: bold;
  color: #111827;
  margin-bottom: 2rem;
  text-align: center;
`;

const LinkGrid = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
`;

const StyledLink = styled(Link)`
  width: 200px;
  height: 200px;
  background-image: url(${props => props.bgImage});
  background-size: cover;
  background-position: center;
  border-radius: 0.5rem;
  overflow: hidden;
  position: relative;
  text-decoration: none;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const LinkOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const LinkText = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  text-align: center;
`;

// Muted and inert - shown while the speaker flow is still being finished, so it
// reads as "on the way" rather than missing. Restore a Link here to enable it.
const DisabledTile = styled.div`
  width: 200px;
  height: 200px;
  background-color: #A8CBDD;
  border-radius: 0.5rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  cursor: not-allowed;
  user-select: none;
`;

const ComingSoonBadge = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #47ABDB;
  background-color: white;
  border-radius: 999px;
  padding: 0.25rem 0.75rem;
`;

const SolidTileText = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: white;
  text-align: center;
  padding: 0 1rem;
`;

const AdminDashboard = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      await api.get('/api/protected');
      setIsLoggedIn(true);
    } catch (error) {
      console.error('Error checking auth status:', error);
      setIsLoggedIn(false);
      navigate('/admin/login');
    }
  };

  if (!isLoggedIn) {
    return (
      <PageContainer>
        <MainContent>
          <Link to="/" style={{ color: '#3b82f6', textDecoration: 'none' }}>
            Return to Home
          </Link>
        </MainContent>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <MainContent>
        <DashboardTitle>Admin Dashboard</DashboardTitle>
        <LinkGrid>
          <StyledLink to="/admin/user-management" bgImage={userManagementImage}>
            <LinkOverlay>
              <LinkText>User Management</LinkText>
            </LinkOverlay>
          </StyledLink>
          <StyledLink to="/admin/content-management" bgImage={contentManagementImage}>
            <LinkOverlay>
              <LinkText>Content Management</LinkText>
            </LinkOverlay>
          </StyledLink>
          <DisabledTile title="Speaker Management is coming soon">
            <Mic color="white" size={40} />
            <SolidTileText>Speaker Management</SolidTileText>
            <ComingSoonBadge>Coming soon</ComingSoonBadge>
          </DisabledTile>
        </LinkGrid>
      </MainContent>
    </PageContainer>
  );
};

export default AdminDashboard;